import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { announcementsAPI } from '@/lib/api';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';
import { toast } from 'sonner';
import {
  Search, Users, Calendar, MapPin, Plus, Trash2,
  MessageSquare, Clock, UserPlus, CalendarSearch, Megaphone, Filter
} from 'lucide-react';
import { getInitials, getRelativeTime } from '@/lib/utils';

const TYPE_INFO = {
  looking_for_players: {
    icon: UserPlus,
    label: 'Cerco Giocatori',
    chip: 'bg-blue-500/15 text-blue-600 dark:text-blue-300 ring-blue-500/30',
    accent: 'from-blue-500 to-blue-600',
    soft: 'from-blue-500/10 via-blue-500/5 to-transparent',
  },
  looking_for_match: {
    icon: CalendarSearch,
    label: 'Cerco Partita',
    chip: 'bg-emerald-500/15 text-emerald-600 dark:text-emerald-300 ring-emerald-500/30',
    accent: 'from-emerald-500 to-emerald-600',
    soft: 'from-emerald-500/10 via-emerald-500/5 to-transparent',
  },
  looking_for_team: {
    icon: Users,
    label: 'Cerco Squadra',
    chip: 'bg-violet-500/15 text-violet-600 dark:text-violet-300 ring-violet-500/30',
    accent: 'from-violet-500 to-purple-600',
    soft: 'from-violet-500/10 via-violet-500/5 to-transparent',
  },
};

const DEFAULT_TYPE = {
  icon: MessageSquare,
  label: 'Annuncio',
  chip: 'bg-slate-500/15 text-slate-600 dark:text-slate-300 ring-slate-500/30',
  accent: 'from-slate-500 to-slate-600',
  soft: 'from-slate-500/10 via-slate-500/5 to-transparent',
};

const TYPE_FILTERS = [
  { value: 'all', label: 'Tutti', icon: Megaphone },
  { value: 'looking_for_players', label: 'Cerco Giocatori', icon: UserPlus },
  { value: 'looking_for_match', label: 'Cerco Partita', icon: CalendarSearch },
  { value: 'looking_for_team', label: 'Cerco Squadra', icon: Users },
];

const FilterChip = ({ active, onClick, icon: Icon, label, count }) => (
  <button
    type="button"
    onClick={onClick}
    className={`shrink-0 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border text-sm transition-all ${
      active
        ? 'bg-primary text-primary-foreground border-primary shadow-md'
        : 'bg-card hover:border-primary/50 hover:shadow-sm'
    }`}
  >
    <Icon className="h-3.5 w-3.5" />
    <span>{label}</span>
    {typeof count === 'number' && (
      <span className={`text-[11px] px-1.5 rounded-full tabular-nums ${active ? 'bg-white/25' : 'bg-muted'}`}>
        {count}
      </span>
    )}
  </button>
);

const AnnouncementCard = ({ announcement, isOwner, onDelete }) => {
  const info = TYPE_INFO[announcement.type] || DEFAULT_TYPE;
  const Icon = info.icon;

  return (
    <Card className="overflow-hidden group hover:shadow-lg hover:-translate-y-0.5 transition-all">
      <CardContent className="p-0">
        {/* Top accent strip */}
        <div className={`h-1 bg-gradient-to-r ${info.accent}`} />

        <div className="p-5 relative">
          {/* Decorative gradient corner */}
          <div className={`pointer-events-none absolute top-0 right-0 w-32 h-32 rounded-bl-full bg-gradient-to-bl ${info.soft}`} />

          <div className="relative flex items-start gap-3 mb-3">
            <div className={`shrink-0 h-11 w-11 rounded-xl flex items-center justify-center bg-gradient-to-br ${info.accent} text-white shadow-md`}>
              <Icon className="h-5 w-5" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap mb-0.5">
                <span className={`text-[11px] uppercase tracking-widest font-semibold px-2 py-0.5 rounded-full ring-1 ${info.chip}`}>
                  {info.label}
                </span>
                {announcement.sport_name && (
                  <Badge variant="secondary" className="text-[11px]">{announcement.sport_name}</Badge>
                )}
              </div>
              <h3 className="font-bold text-lg leading-tight">{announcement.title}</h3>
            </div>
            {isOwner && (
              <Button
                variant="ghost"
                size="icon"
                className="shrink-0 h-8 w-8 text-muted-foreground hover:text-destructive opacity-0 group-hover:opacity-100 transition-opacity"
                onClick={() => onDelete(announcement.announcement_id)}
              >
                <Trash2 className="h-4 w-4" />
              </Button>
            )}
          </div>

          <p className="relative text-sm text-foreground/80 leading-relaxed mb-4 line-clamp-3">
            {announcement.content}
          </p>

          <div className="relative flex flex-wrap items-center gap-3 text-xs text-muted-foreground mb-4">
            {announcement.city && (
              <span className="inline-flex items-center gap-1">
                <MapPin className="h-3.5 w-3.5" />
                {announcement.city}
              </span>
            )}
            {announcement.date_needed && (
              <span className="inline-flex items-center gap-1">
                <Calendar className="h-3.5 w-3.5" />
                {new Date(announcement.date_needed).toLocaleDateString('it-IT')}
              </span>
            )}
            {announcement.players_needed && (
              <span className="inline-flex items-center gap-1">
                <Users className="h-3.5 w-3.5" />
                {announcement.players_needed} giocatori
              </span>
            )}
          </div>

          <div className="relative flex items-center justify-between pt-3 border-t">
            <div className="flex items-center gap-2.5">
              <Avatar className="h-9 w-9 ring-2 ring-background">
                <AvatarImage src={announcement.author_picture} />
                <AvatarFallback className="text-xs">{getInitials(announcement.author_name)}</AvatarFallback>
              </Avatar>
              <div>
                <p className="text-sm font-medium leading-tight">{announcement.author_name}</p>
                <p className="text-[11px] text-muted-foreground flex items-center gap-1 mt-0.5">
                  <Clock className="h-3 w-3" />
                  {getRelativeTime(announcement.created_at)}
                </p>
              </div>
            </div>
            {!isOwner && (
              <Button size="sm" variant="outline" className="hover:bg-primary hover:text-primary-foreground hover:border-primary transition-colors">
                <MessageSquare className="h-4 w-4 mr-1.5" />
                Contatta
              </Button>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export const AnnouncementsPage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [announcements, setAnnouncements] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [activeType, setActiveType] = useState('all');

  useEffect(() => {
    fetchAnnouncements();
  }, []);

  const fetchAnnouncements = async () => {
    try {
      const res = await announcementsAPI.getAll();
      setAnnouncements(res.data);
    } catch (error) {
      console.error('Error fetching announcements:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (announcementId) => {
    if (!confirm('Sei sicuro di voler eliminare questo annuncio?')) return;

    try {
      await announcementsAPI.delete(announcementId);
      setAnnouncements(prev => prev.filter(a => a.announcement_id !== announcementId));
      toast.success('Annuncio eliminato');
    } catch (error) {
      toast.error('Errore nell\'eliminazione');
    }
  };

  const counts = useMemo(() => {
    const c = { all: announcements.length };
    for (const ann of announcements) {
      c[ann.type] = (c[ann.type] || 0) + 1;
    }
    return c;
  }, [announcements]);

  const filteredAnnouncements = announcements.filter(ann => {
    const q = searchQuery.toLowerCase();
    const matchesSearch =
      ann.title?.toLowerCase().includes(q) ||
      ann.content?.toLowerCase().includes(q) ||
      ann.city?.toLowerCase().includes(q);
    const matchesType = activeType === 'all' || ann.type === activeType;
    return matchesSearch && matchesType;
  });

  return (
    <div className="pb-24" data-testid="announcements-page">
      {/* Hero */}
      <div className="relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-amber-500 via-orange-500 to-rose-500" />
        <div className="pointer-events-none absolute -top-20 -right-32 w-80 h-80 rounded-full bg-white/10 blur-3xl" />
        <div className="pointer-events-none absolute -bottom-32 -left-20 w-72 h-72 rounded-full bg-white/15 blur-3xl" />
        <div className="relative px-4 md:px-8 pt-8 pb-12">
          <div className="max-w-5xl mx-auto">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
              <div>
                <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-white/20 backdrop-blur text-white text-[11px] uppercase tracking-widest font-semibold mb-2">
                  <Megaphone className="h-3 w-3" />
                  Bacheca
                </span>
                <h1 className="text-3xl md:text-4xl font-bold text-white drop-shadow-lg">
                  Trova compagni di gioco
                </h1>
                <p className="text-white/85 mt-1 max-w-md">
                  Cerca giocatori, squadre o partite vicine a te. Pubblica il tuo annuncio in 30 secondi.
                </p>
              </div>
              <Button
                size="lg"
                onClick={() => navigate('/announcements/new')}
                className="bg-white text-foreground hover:bg-white/90 shadow-xl shrink-0"
                data-testid="new-announcement-btn"
              >
                <Plus className="h-4 w-4 mr-2" />
                Nuovo Annuncio
              </Button>
            </div>
          </div>
        </div>
      </div>

      <div className="relative z-10 max-w-5xl mx-auto px-4 md:px-8 -mt-6 space-y-6">
        {/* Search bar (floating) */}
        <Card>
          <CardContent className="p-4 space-y-4">
            <div className="relative">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-muted-foreground" />
              <Input
                placeholder="Cerca per titolo, contenuto o città..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-12 h-12 text-base"
              />
            </div>

            <div className="flex items-center gap-2 overflow-x-auto no-scrollbar -mx-1 px-1">
              <Filter className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
              {TYPE_FILTERS.map(f => (
                <FilterChip
                  key={f.value}
                  active={activeType === f.value}
                  onClick={() => setActiveType(f.value)}
                  icon={f.icon}
                  label={f.label}
                  count={counts[f.value] || 0}
                />
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Feed */}
        {loading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {[1, 2, 3, 4].map(i => <Skeleton key={i} className="h-56" />)}
          </div>
        ) : filteredAnnouncements.length === 0 ? (
          <Card>
            <CardContent className="p-12 text-center">
              <div className="h-14 w-14 mx-auto mb-4 rounded-2xl bg-gradient-to-br from-amber-500 to-orange-500 flex items-center justify-center text-white shadow-lg">
                <MessageSquare className="h-7 w-7" />
              </div>
              <h2 className="text-xl font-semibold mb-1">
                {searchQuery ? 'Nessun annuncio trovato' : 'La bacheca è vuota'}
              </h2>
              <p className="text-muted-foreground mb-5">
                {searchQuery
                  ? 'Prova a modificare la ricerca o cambiare filtro.'
                  : 'Sii il primo a pubblicare un annuncio.'}
              </p>
              <Button onClick={() => navigate('/announcements/new')}>
                <Plus className="h-4 w-4 mr-2" />
                Crea Annuncio
              </Button>
            </CardContent>
          </Card>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {filteredAnnouncements.map(announcement => (
              <AnnouncementCard
                key={announcement.announcement_id}
                announcement={announcement}
                isOwner={announcement.created_by === user?.user_id}
                onDelete={handleDelete}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default AnnouncementsPage;
